import { useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Stepper } from "@/components/studio/Stepper";
import { StepSource } from "@/components/studio/StepSource";
import { StepEdit } from "@/components/studio/StepEdit";
import { StepResult } from "@/components/studio/StepResult";
import { useStudyVersions } from "@/hooks/useStudyVersions";
import { useAuth } from "@/context/AuthContext";

export default function Studio() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [src, setSrc] = useState(null);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const sv = useStudyVersions();
  const { versions } = sv;

  const work = useMemo(() => ({
    title: title || versions.a?.title, mindmap: versions.a, version_b: versions.b, version_c: versions.c,
    extracted_text: text, author: `${user?.nome} ${user?.cognome}`, classe: user?.classe,
  }), [title, versions, text, user]);

  const reset = () => { setStep(1); setSrc(null); setText(""); setTitle(""); sv.reset(); };

  const onExtracted = (r) => { setSrc(r); setText(r.text); setTitle(r.title); setStep(2); };

  const generateMap = async () => {
    const data = await sv.generate("a", text);
    if (!data) return;
    if (data.title) setTitle(data.title);
    setStep(4);
  };

  const save = () => sv.save({ title: work.title, source_type: src.source_type, source_name: src.source_name, extracted_text: text });

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#0D9488] mb-3">Ciao {user?.nome} — Classe {user?.classe}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-gradient tracking-tight">Generatore di Studio</h1>
        </div>
        {step > 1 && (
          <button data-testid="wizard-reset-btn" onClick={reset} className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-300 text-sm font-medium hover:border-zinc-950 transition-colors duration-150">
            <RotateCcw className="w-4 h-4" /> Nuova sessione
          </button>
        )}
      </div>
      <Stepper step={step === 4 && sv.saved ? 5 : step} />
      {step === 1 && <StepSource onExtracted={onExtracted} />}
      {step === 2 && (
        <StepEdit title={title} setTitle={setTitle} text={text} setText={setText} busy={sv.loading.a} onBack={() => setStep(1)} onGenerate={generateMap} />
      )}
      {step === 4 && versions.a && (
        <StepResult
          work={work}
          sv={sv}
          onEdit={() => setStep(2)}
          onRegenerate={() => (sv.ver === "a" ? generateMap() : sv.generate(sv.ver, text))}
          onSelect={(v) => sv.select(v, text)}
          onSave={save}
        />
      )}
    </div>
  );
}
